import React, { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getUrl } from '../components/getSubjectId';
import { BackBtn, SubjectNameGender } from '../components/shared/NavigationButtons';
import ContainerContent from '../components/ContainerContent';
import styled from 'styled-components';
import http from '../components/http/http';
import TemplateV1Entry from './templates/v1/TemplateV1Entry';

const Title = styled.h1`
  font-size: 38px !important;
  font-style: normal;
  font-weight: 700;
  line-height: 52px;
  letter-spacing: 0.8px;
`;

interface ReportData {
  _id: string;
  userId: string;
  biomarkers: any[];
  surveyResponses: any[];
  analysisResult: any;
  templateVersion: number | string;
  sourceFileName: string;
  themeId: number | string;
  createdAt: string;
  updatedAt: string;
}

export default function MyReport() {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const reportId = searchParams.get('id');
  const templateVer = searchParams.get('templateVer');
  const fileName = searchParams.get('fileName');
  const dateStr = searchParams.get('date');
  const [error, setError] = useState<string | null>(null);
  const [displayDate, setDisplayDate] = useState<string>('');
  const [displayFileName, setDisplayFileName] = useState<string>('');
  const [reportData, setReportData] = useState<ReportData | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (dateStr) {
      const d = new Date(dateStr);
      setDisplayDate(d.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' }));
    }
    if (fileName) {
      setDisplayFileName(decodeURIComponent(fileName));
    }
    if (!reportId || !templateVer) {
      setError('Report ID and template version are required');
    }
  }, [reportId, templateVer, fileName, dateStr]);

  useEffect(() => {
    if (!reportId) return;
    setLoading(true);
    http.get(`/api/my-reports/${reportId}`, { raw: true }).then((res: any) => {
      if (res?.success && res?.data) {
        setReportData(res.data);
      }
    }).finally(() => setLoading(false));
  }, [reportId]);

  if (error) {
    return (
      <ContainerContent>
        <div style={{ padding: '40px 0', textAlign: 'center', color: '#888' }}>
          {error}
        </div>
        <Link to={getUrl('my-reports')} className={'mytitle'} style={{ width: 'auto', display: 'inline-block' }}>
          <BackBtn />
          <SubjectNameGender />
        </Link>
      </ContainerContent>
    );
  }

  return (
    <ContainerContent>
      <Title>{t('myReport')}</Title>
      <h2>{displayDate && displayFileName ? `${displayDate}, ${displayFileName}` : ``}</h2>
      <div className={'wrapTitleNav'}>
        <Link to={getUrl('my-reports')} className={'mytitle'} style={{ width: 'auto' }}>
          <BackBtn />
          <SubjectNameGender />
        </Link>
      </div>
      {loading ? (
        <div style={{ padding: '40px 0', textAlign: 'center', color: '#888' }}>Loading...</div>
      ) : reportData ? (
        <>
          {Number(reportData.templateVersion) === 1 && reportData.analysisResult?.info ? (
            <TemplateV1Entry info={reportData.analysisResult.info} themeId={Number(reportData.themeId)} />
          ) : (
            <div style={{ padding: '40px 0', textAlign: 'center', color: '#888' }}>
              Unknown template version: {reportData.templateVersion}
            </div>
          )}
        </>
      ) : (
        <div style={{ padding: '40px 0', textAlign: 'center', color: '#888' }}>
          No data
        </div>
      )}
    </ContainerContent>
  );
}
